import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CILogo } from '../../components/CILogo';
import { 
  Users, Building2, Package, Tag, Shield, Settings, LogOut,
  TrendingUp, AlertCircle, CheckCircle, XCircle, Search, Plus,
  ChevronDown, Eye, Edit, Trash2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Unauthorized');

      const data = await response.json();
      if (data.role !== 'super_admin') {
        toast.error('Super admin access required');
        navigate('/admin/login');
        return;
      }

      setUser(data);
      loadData(token);
    } catch (error) {
      sessionStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const loadData = async (token) => {
    try {
      const [statsRes, usersRes, merchantsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/super/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/super/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/super/merchants`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/super/audit-logs?limit=50`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
      if (merchantsRes.ok) {
        const merchantsData = await merchantsRes.json();
        setMerchants(merchantsData.merchants || []);
      }
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setAuditLogs(logsData.logs || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = sessionStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error("Error:", e); }
    sessionStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('admin_token');
    const formData = new FormData(e.target);

    try {
      const response = await fetch(`${API_URL}/api/admin/super/create-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.get('email'),
          name: formData.get('name'),
          password: formData.get('password'),
          role: formData.get('role')
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success('Admin user created successfully');
      setShowCreateModal(false);
      loadData(token);
    } catch (error) {
      toast.error(error.message || 'Failed to create admin');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    const token = sessionStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/super/user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to deactivate');

      toast.success('User deactivated');
      loadData(token);
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3EF1C7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#9CB0C7]">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A1628]/95 backdrop-blur-sm border-b border-[#1E3A5F]">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CILogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-white">Super Admin</h1>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm font-medium border border-red-500/30">
              <Shield className="w-4 h-4 inline mr-1" />
              Super Admin
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#22304A] pb-4">
          {['overview', 'users', 'merchants', 'agents', 'audit'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-[#7A5CFF] text-white'
                  : 'text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]'
              }`}
            >
              {tab === 'overview' && <TrendingUp className="w-4 h-4 inline mr-2" />}
              {tab === 'users' && <Users className="w-4 h-4 inline mr-2" />}
              {tab === 'merchants' && <Building2 className="w-4 h-4 inline mr-2" />}
              {tab === 'agents' && <Shield className="w-4 h-4 inline mr-2" />}
              {tab === 'audit' && <Eye className="w-4 h-4 inline mr-2" />}
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Total Users</p>
                  <Users className="w-5 h-5 text-[#3EF1C7]" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.users?.total || 0}</p>
                <p className="text-xs text-[#8CF78E] mt-1">{stats.users?.active || 0} active</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Merchants</p>
                  <Building2 className="w-5 h-5 text-[#7A5CFF]" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.merchants || 0}</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Products</p>
                  <Package className="w-5 h-5 text-[#FFC857]" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.products || 0}</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Active Deals</p>
                  <Tag className="w-5 h-5 text-[#8CF78E]" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.deals || 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Admin Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#9CB0C7]">Super Admins</span>
                    <span className="text-white font-bold">{stats.users?.super_admins || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#9CB0C7]">Merchant Admins</span>
                    <span className="text-white font-bold">{stats.users?.merchant_admins || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#9CB0C7]">Regular Users</span>
                    <span className="text-white font-bold">{(stats.users?.total || 0) - (stats.users?.super_admins || 0) - (stats.users?.merchant_admins || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CB0C7]" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Admin
              </button>
            </div>

            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#111A2E]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22304A]">
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="hover:bg-[#111A2E]/40">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-white">{u.name}</p>
                          <p className="text-sm text-[#9CB0C7]">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          u.role === 'super_admin' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                          u.role === 'merchant_admin' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' :
                          'bg-gray-500/10 text-gray-400 border border-gray-500/30'
                        }`}>
                          {u.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {u.is_active ? (
                          <span className="flex items-center gap-1 text-[#8CF78E]">
                            <CheckCircle className="w-4 h-4" /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[#F55454]">
                            <XCircle className="w-4 h-4" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button className="p-2 rounded hover:bg-[#1E3A5F] text-[#9CB0C7] hover:text-white">
                            <Edit className="w-4 h-4" />
                          </button>
                          {u.user_id !== user?.user_id && (
                            <button
                              onClick={() => handleDeactivateUser(u.user_id)}
                              className="p-2 rounded hover:bg-red-500/10 text-[#9CB0C7] hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Merchants Tab */}
        {activeTab === 'merchants' && (
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Merchant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {merchants.map((m) => (
                  <tr key={m._id} className="hover:bg-[#111A2E]/40">
                    <td className="px-6 py-4">
                      <p className="font-medium text-white">{m.name || 'Unnamed Store'}</p>
                    </td>
                    <td className="px-6 py-4 text-[#9CB0C7]">{m.email}</td>
                    <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Agent Fleet Tab */}
        {activeTab === 'agents' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-4">
                <p className="text-xs text-[#9CB0C7]">Total Agent Configs</p>
                <p className="text-2xl font-bold text-white mt-1">—</p>
                <p className="text-xs text-[#6B8199] mt-1">Across all merchants</p>
              </div>
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-4">
                <p className="text-xs text-[#9CB0C7]">Active Agents</p>
                <p className="text-2xl font-bold text-[#3EF1C7] mt-1">—</p>
                <p className="text-xs text-[#6B8199] mt-1">Currently running</p>
              </div>
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-4">
                <p className="text-xs text-[#9CB0C7]">Decisions (24h)</p>
                <p className="text-2xl font-bold text-[#7A5CFF] mt-1">—</p>
                <p className="text-xs text-[#6B8199] mt-1">Agent decisions made</p>
              </div>
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-4">
                <p className="text-xs text-[#9CB0C7]">Scheduled Agents</p>
                <p className="text-2xl font-bold text-[#FFC857] mt-1">—</p>
                <p className="text-xs text-[#6B8199] mt-1">Active schedules</p>
              </div>
            </div>

            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Platform Agent Controls</h3>
              <p className="text-sm text-[#9CB0C7] mb-4">
                Manage platform-level defaults, budget caps, and agent kill switches.
                Access the full Agent Fleet API at <code className="text-[#7A5CFF]">/api/admin/super/agents/overview</code>.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded border border-[#22304A] bg-[#0B1120]">
                  <h4 className="text-sm font-medium text-white mb-2">Budget Caps</h4>
                  <p className="text-xs text-[#6B8199]">Set maximum monthly credit budgets per agent type across all merchants.</p>
                  <code className="text-[10px] text-[#7A5CFF] mt-2 block">PATCH /api/admin/super/agents/defaults</code>
                </div>
                <div className="p-4 rounded border border-[#22304A] bg-[#0B1120]">
                  <h4 className="text-sm font-medium text-white mb-2">Schedule Ranges</h4>
                  <p className="text-xs text-[#6B8199]">Set min/max interval ranges merchants can configure for agent schedules.</p>
                  <code className="text-[10px] text-[#7A5CFF] mt-2 block">PATCH /api/admin/super/agents/defaults</code>
                </div>
                <div className="p-4 rounded border border-[#22304A] bg-[#0B1120]">
                  <h4 className="text-sm font-medium text-white mb-2">Kill Switch</h4>
                  <p className="text-xs text-[#6B8199]">Force-disable an agent type across ALL merchants instantly.</p>
                  <code className="text-[10px] text-[#F55454] mt-2 block">POST /api/admin/super/agents/&#123;type&#125;/force-disable</code>
                </div>
                <div className="p-4 rounded border border-[#22304A] bg-[#0B1120]">
                  <h4 className="text-sm font-medium text-white mb-2">Agent Audit Trail</h4>
                  <p className="text-xs text-[#6B8199]">Cross-merchant agent decision audit with filtering by agent type and merchant.</p>
                  <code className="text-[10px] text-[#7A5CFF] mt-2 block">GET /api/admin/super/agents/audit</code>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Merchant Drill-Down</h3>
              <p className="text-sm text-[#9CB0C7] mb-4">View any merchant's agent org chart, budgets, and schedules.</p>
              <div className="rounded-lg border border-[#22304A] bg-[#0B1120] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#111A2E]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7]">Merchant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7]">Agents</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7]">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#22304A]">
                    {merchants.slice(0, 10).map((m, i) => (
                      <tr key={i} className="hover:bg-[#111A2E]/40">
                        <td className="px-4 py-3 text-sm text-white">{m.name || m.email || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm text-[#9CB0C7]">—</td>
                        <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-[#3EF1C7]/10 text-[#3EF1C7]">active</span></td>
                        <td className="px-4 py-3">
                          <code className="text-[10px] text-[#7A5CFF]">GET /api/admin/super/agents/merchant/{m._id}/org-chart</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 overflow-hidden">
            <div className="p-4 border-b border-[#22304A]">
              <h3 className="text-lg font-semibold text-white">Audit Logs</h3>
            </div>
            <div className="divide-y divide-[#22304A] max-h-[600px] overflow-y-auto">
              {auditLogs.map((log, i) => (
                <div key={i} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-medium">{log.action}</span>
                    <span className="text-xs text-[#9CB0C7]">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                    </span>
                  </div>
                  <p className="text-sm text-[#9CB0C7]">
                    Admin: {log.admin_id?.slice(0, 8)}... → Target: {log.target?.slice(0, 8)}...
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-6">Create Admin User</h2>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Role</label>
                <select
                  name="role"
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                >
                  <option value="merchant_admin">Merchant Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-[#22304A] text-white hover:bg-[#1A2437]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
